import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, GraduationCap, HeartPulse, Stethoscope, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, MaskLine } from "../components/Reveal";
import AppCallout from "../components/AppCallout";
import BlogSection from "../components/BlogSection";
import InstagramReels from "../components/InstagramReels";
import HostelLife from "../components/HostelLife";
import { LABS, PARTNERS, PLACEMENTS, RECOGNITIONS, PROGRAM_SLUGS } from "../data/siteContent";
import heroBuilding from "../assets/hero-building.png";

const HERO_IMG = heroBuilding;

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (inView) {
      const controls = animate(0, to, {
        duration: 1.8,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => setVal(Math.floor(v)),
      });
      return () => controls.stop();
    }
  }, [inView, to]);
  return (
    <span ref={ref} className="font-mono">
      {val}
      {suffix}
    </span>
  );
}

const iconFor = (short) => {
  if (short?.includes("B.Sc")) return GraduationCap;
  if (short?.includes("GNM")) return Stethoscope;
  return HeartPulse;
};

export default function Home() {
  const [courses, setCourses] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — split editorial */}
      <section ref={heroRef} className="relative min-h-screen bg-navy-dark overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative grid lg:grid-cols-[1fr_1.15fr] min-h-screen">
          {/* Text column on solid navy */}
          <div className="flex items-center px-5 sm:px-10 lg:px-16 pt-36 pb-16 lg:py-24 order-2 lg:order-1">
            <div className="max-w-xl">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="flex items-center gap-3 mb-8"
              >
                <span className="h-px w-12 bg-gold" />
                <span className="text-xs tracking-[0.3em] uppercase text-gold font-mono">
                  Admissions Open · 2026-27
                </span>
              </motion.div>

              <h1 className="font-serif text-sand leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
                <MaskLine delay={0.15}>Where compassion</MaskLine>
                <MaskLine delay={0.3}>
                  becomes a <span className="italic text-gold-gradient">calling</span>
                </MaskLine>
                <MaskLine delay={0.45}>in modern nursing.</MaskLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.8 }}
                className="mt-8 text-sand/70 text-base sm:text-lg max-w-md leading-relaxed"
              >
                Excellence in ANM, GNM and B.Sc Nursing education — nurturing skilled,
                compassionate and globally competent healthcare professionals.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/admissions"
                  data-testid="hero-apply-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gold text-navy-dark font-bold px-7 py-4 transition-transform duration-300 hover:scale-105"
                >
                  Admissions 2026-27 <ArrowUpRight size={18} />
                </Link>
                <Link
                  to="/courses"
                  data-testid="hero-courses-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-7 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  Explore Programs
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Image column — prominent & clean */}
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative min-h-[55vh] lg:min-h-screen order-1 lg:order-2 overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="SVS College of Nursing campus at night"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* seam blend into navy text column */}
            <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-navy-dark to-transparent hidden lg:block" />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-navy-dark/70 to-transparent lg:hidden" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
            <div className="absolute inset-y-0 left-0 w-px bg-gold/40 hidden lg:block" />
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-navy py-6 relative overflow-hidden" data-testid="marquee-section">
        <div className="grain-overlay" />
        <Marquee speed={40} gradient={false} className="relative">
          {["ADMISSIONS OPEN 2026-27", "B.Sc NURSING", "GNM", "ANM", "INC RECOGNISED", "CLINICAL EXCELLENCE"].map(
            (t, i) => (
              <span key={i} className="mx-8 font-serif text-2xl sm:text-3xl text-sand/90 flex items-center gap-8">
                {t} <span className="text-gold text-xl">✦</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="bg-sand py-24 lg:py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Our Ethos</span>
                <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">
                  A promise of <span className="italic text-gold-gradient">care</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 space-y-12">
              {[
                {
                  n: "01",
                  t: "Vision",
                  d: "To be a premier centre of nursing excellence, shaping globally competent, compassionate and ethical healthcare professionals who lead positive transformation.",
                },
                {
                  n: "02",
                  t: "Mission",
                  d: "To deliver excellence in nursing education through comprehensive ANM, GNM and B.Sc Nursing programs that meet the evolving demands of the healthcare sector.",
                },
                {
                  n: "03",
                  t: "Values",
                  d: "Compassion, integrity and lifelong learning — cultivated through mentorship, hands-on clinical training and a culture of service to community.",
                },
              ].map((c, i) => (
                <Reveal key={c.n} delay={i * 0.1}>
                  <div className="grid grid-cols-[auto,1fr] gap-6 sm:gap-10 border-t border-navy/10 pt-8">
                    <div className="font-serif text-5xl sm:text-6xl text-gold/40">{c.n}</div>
                    <div>
                      <h3 className="font-serif text-2xl text-navy mb-3">{c.t}</h3>
                      <p className="text-navy/60 leading-relaxed max-w-2xl">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-navy py-20 relative overflow-hidden" data-testid="stats-section">
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { n: 3, s: "+", l: "Nursing Programs" },
            { n: 180, s: "+", l: "Annual Seats" },
            { n: 30, s: "+", l: "Expert Faculty" },
            { n: 100, s: "%", l: "Clinical Training" },
          ].map((st, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="text-center lg:text-left">
                <div className="text-4xl sm:text-5xl font-serif text-gold-gradient">
                  <Counter to={st.n} suffix={st.s} />
                </div>
                <div className="mt-2 text-xs sm:text-sm tracking-[0.15em] uppercase text-sand/60">{st.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section className="bg-sand py-24 lg:py-32" data-testid="courses-preview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Programs</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Pathways into nursing</h2>
            </Reveal>
            <Reveal>
              <Link to="/courses" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors">
                View all programs <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => {
              const Icon = iconFor(c.short_name);
              return (
                <Reveal key={c.id} delay={i * 0.1}>
                  <Link to={`/programs/${PROGRAM_SLUGS[c.short_name] || ""}`} className="block h-full" data-testid={`course-card-link-${c.short_name}`}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="group relative bg-navy text-sand rounded-[2px] p-8 h-full overflow-hidden cursor-pointer"
                    data-testid={`course-card-${c.short_name}`}
                  >
                    <div className="grain-overlay" />
                    <div className="relative">
                      <div className="flex items-center justify-between mb-8">
                        <Icon size={34} className="text-gold" strokeWidth={1.4} />
                        <span className="text-xs font-mono tracking-widest text-sand/50">{c.duration}</span>
                      </div>
                      <h3 className="font-serif text-2xl mb-3">{c.title}</h3>
                      <p className="text-sand/60 text-sm leading-relaxed mb-6 line-clamp-4">{c.description}</p>
                      <div className="flex items-center gap-2 text-gold text-sm font-semibold">
                        {c.seats}
                      </div>
                    </div>
                  </motion.div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR LABS */}
      <section className="bg-white py-24 lg:py-32" data-testid="labs-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Facilities</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Our laboratories</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                State-of-the-art laboratories where theory meets practice — from human anatomy to
                hands-on clinical skills, our students train in real, well-equipped environments.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LABS.map((lab, i) => (
              <Reveal key={lab.name} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="group relative overflow-hidden rounded-[2px] h-full bg-navy text-sand"
                  data-testid={`lab-card-${i}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={lab.img}
                      alt={lab.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
                  </div>
                  <div className="p-6 sm:p-8">
                    <span className="text-xs font-mono tracking-[0.2em] uppercase text-gold">{lab.tag}</span>
                    <h3 className="font-serif text-2xl sm:text-3xl text-sand mt-1">{lab.name}</h3>
                    <p className="text-sand/75 text-sm mt-2 max-w-md leading-relaxed">{lab.desc}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12">
              <Link
                to="/gallery"
                data-testid="labs-gallery-btn"
                className="inline-flex items-center gap-2 rounded-full bg-navy text-sand font-semibold px-7 py-3.5 transition-transform duration-300 hover:scale-105 hover:bg-navy-light"
              >
                View full gallery <ArrowRight size={18} className="text-gold" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOSTEL LIFE */}
      <HostelLife />

      {/* OUR RECOGNITION & AFFILIATIONS */}
      <section className="bg-sand py-24 lg:py-32" data-testid="recognition-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Accreditation</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Our recognition &amp; affiliations</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                SVS College of Nursing is recognised and affiliated with premier national and state
                bodies — ensuring the quality, credibility and validity of our nursing programs.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {RECOGNITIONS.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.08}>
                <div className="bg-white border border-navy/10 rounded-[2px] p-6 sm:p-8 h-full text-center flex flex-col items-center" data-testid={`recognition-${i}`}>
                  <div className="h-28 flex items-center justify-center">
                    <img
                      src={r.img}
                      alt={r.name}
                      loading="lazy"
                      className="max-h-28 max-w-full object-contain transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <h3 className="font-serif text-base sm:text-lg text-navy mt-6 leading-snug">{r.name}</h3>
                  <p className="text-navy/55 text-xs sm:text-sm mt-2 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PLACEMENT PARTNERS */}
      <section className="bg-sand py-24 lg:py-28" data-testid="placements-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-14">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Careers</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">Placement partners</h2>
              <p className="text-navy/60 mt-5 max-w-xl leading-relaxed">
                Where our graduates begin their nursing careers — leading hospitals and healthcare
                groups that hire directly from SVS.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <Link
                to="/placements"
                data-testid="placement-badge"
                className="relative inline-flex items-center gap-4 rounded-full bg-navy text-sand pl-4 pr-6 py-3 shadow-[0_10px_30px_rgba(10,30,77,0.25)] transition-transform duration-300 hover:scale-105"
              >
                <div className="grain-overlay rounded-full" />
                <div className="relative h-11 w-11 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center">
                  <CheckCircle2 size={22} className="text-gold" />
                </div>
                <div className="relative leading-tight">
                  <div className="font-serif text-2xl text-gold-gradient">100%</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-sand/70">Placement Guaranteed</div>
                </div>
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {PLACEMENTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white border border-navy/10 rounded-[2px] h-full flex flex-col items-center justify-center p-6 text-center"
                  data-testid={`placement-${i}`}
                  title={p.name}
                >
                  <div className="h-24 w-full flex items-center justify-center">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="max-h-24 max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-4 text-xs font-medium text-navy/60 leading-snug">{p.name}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR CLINICAL PARTNERS */}
      <section className="bg-sand py-24 lg:py-32" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Partnerships</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Our clinical partners</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                We collaborate with leading hospitals and research centres to give our students
                real-world clinical exposure, supervised postings and hands-on training.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white border border-navy/10 rounded-[2px] h-full flex flex-col items-center justify-center p-6 text-center"
                  data-testid={`partner-${i}`}
                  title={p.name}
                >
                  <div className="h-24 w-full flex items-center justify-center">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="max-h-24 max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-4 text-xs font-medium text-navy/60 leading-snug">{p.name}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM REELS */}
      <InstagramReels />

      {/* BLOGS */}
      <BlogSection />

      {/* GET THE APP */}
      <AppCallout />

      {/* CTA */}
      <section className="bg-navy relative overflow-hidden py-24 lg:py-32" data-testid="home-cta">
        <div className="grain-overlay" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-6xl text-sand leading-tight">
              Begin your <span className="italic text-gold-gradient">nursing journey</span>
            </h2>
            <p className="mt-6 text-sand/70 max-w-xl mx-auto leading-relaxed">
              Applications for the 2026-27 session are now open. Take the first step towards a
              rewarding career in healthcare.
            </p>
            <Link
              to="/admissions"
              data-testid="cta-apply-btn"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Apply Now <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
